import { useCallback } from "react";

import DeleteIcon from "@mui/icons-material/DeleteForever";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import CardActions from "@mui/material/CardActions";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

import {
  AchPaymentMethod,
  CreditCardPaymentMethod,
} from "@covertree/agent-portal-graphql";
import RadioButton from "@covertree/ui/buttons/RadioButton";
import {
  isCreditCardPaymentMethod,
  mapAchPaymentMethodName,
  mapCreditCardPaymentMethodExpiration,
  mapCreditCardPaymentMethodName,
} from "../../../../utils";

type PaymentMethodType = CreditCardPaymentMethod | AchPaymentMethod;

interface Props {
  method: PaymentMethodType;
  selected: boolean;
  disabled?: boolean;
  onClick(): void;
  onDelete(): void;
}
export function PaymentMethodItem({
  method,
  disabled,
  selected,
  onClick,
  onDelete,
}: Props) {
  const onDeleteClick = useCallback(
    (ev: React.MouseEvent<HTMLButtonElement>) => {
      ev.stopPropagation();
      onDelete();
    },
    [onDelete]
  );

  const name = isCreditCardPaymentMethod(method)
    ? mapCreditCardPaymentMethodName(method)
    : mapAchPaymentMethodName(method);

  const title = `${name}${method.default ? " (default)" : ""}`;

  const subtitle = isCreditCardPaymentMethod(method)
    ? mapCreditCardPaymentMethodExpiration(method)
    : undefined;

  return (
    <Card
      onClick={!disabled ? onClick : undefined}
      sx={[
        { cursor: "pointer" },
        selected && { borderColor: "primary.main" },
        disabled && { opacity: 0.6 },
      ]}>
      <Stack px={3} py={2} direction="row" spacing={2} alignItems="center">
        <CardActions sx={{ padding: 0 }}>
          <RadioButton checked={selected} />
        </CardActions>
        <Stack
          direction="row"
          flex={1}
          alignItems="center"
          justifyContent="space-between">
          <Stack>
            <CardHeader title={title} />
            {subtitle && (
              <Typography variant="body2" color="text.disabled">
                {subtitle}
              </Typography>
            )}
          </Stack>
          {!method.default && (
            <IconButton
              onClick={onDeleteClick}
              disabled={disabled}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                height: 40,
                width: 40,
              }}>
              <DeleteIcon />
            </IconButton>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}
